import React, { useEffect, useState } from "react";
import ResponsiveModalAbsolute from "../../components/ResponsiveModalAbsolute";
import ToggleButton from "../../components/helpers/ToggleButton";
import { useAppSelector } from "../../hooks/hooks";
import { fetchAxiosToken } from "../../helpers/fetchAxiosToken";
import Swal from "sweetalert2";
import Spinner from "../../components/helpers/Spinner";
import { useParams } from "react-router-dom";
import { useDispatchDominio } from "../../hooks/useDispatchDominio";

interface Props {
    modalVisiblePagoReserva: boolean;
    setModalVisiblePagoReserva: (v: boolean) => void;
}

export const ModalEditPagoReserva: React.FC<Props> = ({
    modalVisiblePagoReserva,
    setModalVisiblePagoReserva,
}) => {
    const { idCasa } = useParams();
    const dispatchDominio = useDispatchDominio(
        idCasa && Number(idCasa) > 0 ? idCasa : undefined
    );

    const house = useAppSelector((s) => s.landing_house.house);
    const casa_id: number = house?.id;

    // Estado UI
    const [saving, setSaving] = useState(false);

    // Estado de negocio (booleans <-> "Si"/"No")
    const [pagoPrereserva, setPagoPrereserva] = useState<boolean>(
        house.pago_prereserva === "Si"
    );
    const [dosPagos, setDosPagos] = useState<boolean>(house.dos_pagos === "Si");
    const [porcentajePrimerPago, setPorcentajePrimerPago] = useState<string>(
        house.porcentaje_primer_pago
    );

    console.log("house", house);
    console.log({
        pagoPrereserva,
        dosPagos,
        porcentajePrimerPago,
    });
    useEffect(() => {
        if (!house) return;

        setPagoPrereserva(house.pago_prereserva === "Si");
        setDosPagos(house.dos_pagos === "Si");
        setPorcentajePrimerPago(house.porcentaje_primer_pago);
    }, [house, modalVisiblePagoReserva]);

    const handleSave = async () => {
        // Validaciones
        if (dosPagos) {
            const n = Number(porcentajePrimerPago);
            if (
                Number.isNaN(n) ||
                !Number.isFinite(n) ||
                !/^\d{1,3}$/.test(String(porcentajePrimerPago)) ||
                n < 1 ||
                n > 99
            ) {
                Swal.fire({
                    confirmButtonColor: "#029BFC",
                    iconColor: "#029BFC",
                    title: "Dato inválido",
                    text: "El porcentaje del primer pago debe estar entre 1% y 99%.",
                    icon: "error",
                    confirmButtonText: "Ok",
                });
                return;
            }
        }

        const body = {
            pago_prereserva: pagoPrereserva ? "Si" : "No",
            dos_pagos: dosPagos ? "Si" : "No",
            porcentaje_primer_pago: dosPagos
                ? String(Number(porcentajePrimerPago))
                : "0",
        };

        try {
            setSaving(true);
            await fetchAxiosToken({
                url: `/casa/putPagoReserva/${casa_id}`,
                method: "put",
                body,
            });

            await dispatchDominio();

            setTimeout(() => {
                Swal.fire({
                    confirmButtonColor: "#029BFC",
                    iconColor: "#029BFC",
                    title: "Actualizado",
                    text: "Preferencias de pago actualizadas con éxito.",
                    icon: "success",
                    confirmButtonText: "Ok",
                });

                setModalVisiblePagoReserva(false);
            }, 2000);
        } catch (err: any) {
            const msg =
                err?.response?.data?.message ??
                "No se pudo actualizar la configuración de pago.";
            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Error",
                text: String(msg),
                icon: "error",
                confirmButtonText: "Ok",
            });
        } finally {
            setTimeout(() => {
                setSaving(false);
            }, 2000);
        }
    };

    return (
        <ResponsiveModalAbsolute
            modalVisible={modalVisiblePagoReserva}
            setModalVisible={setModalVisiblePagoReserva}>
            <div className="max-w-lg p-4 mx-auto">
                <h2 className="text-base font-medium text-center">
                    Pago de reserva
                </h2>
                <hr className="w-full mb-4 bg-gray-300" />

                {!house ? (
                    <Spinner />
                ) : (
                    <div className="space-y-4">
                        {/* Toggle Pago Pre-reserva */}
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[#3C3C3C] font-medium 2xl:text-base text-sm">
                                    Habilitar pago pre-reserva
                                </p>
                                <p className="text-xs text-gray-500">
                                    Permite pagar una parte para bloquear la
                                    reserva.
                                </p>
                            </div>
                            <ToggleButton
                                isToggled={pagoPrereserva}
                                onChangeToggle={setPagoPrereserva}
                            />
                        </div>

                        {/* Toggle Dos Pagos */}
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-[#3C3C3C] font-medium 2xl:text-base text-sm">
                                    Aceptar 2 pagos
                                </p>
                                <p className="text-xs text-gray-500">
                                    Divide el pago en un anticipo y el resto al
                                    check-in/checkout.
                                </p>
                            </div>
                            <ToggleButton
                                isToggled={dosPagos}
                                onChangeToggle={setDosPagos}
                            />
                        </div>

                        {/* Porcentaje primer pago */}
                        <div
                            className={`${
                                dosPagos ? "opacity-100" : "opacity-60"
                            } transition-opacity`}>
                            <div className="w-full my-1 text-left">
                                <header className="mb-2 text-[#3C3C3C] 2xl:text-base text-sm">
                                    Porcentaje del primer pago{" "}
                                    {dosPagos
                                        ? "(requerido)"
                                        : "(deshabilitado)"}
                                    :
                                </header>
                            </div>
                            <div className="flex items-center gap-2">
                                <div
                                    className={`w-full px-4 rounded-lg border border-[#E9E9E9] ${
                                        dosPagos ? "" : "bg-gray-50"
                                    }`}>
                                    <input
                                        type="number"
                                        min={1}
                                        max={99}
                                        inputMode="numeric"
                                        disabled={!dosPagos}
                                        value={
                                            dosPagos
                                                ? porcentajePrimerPago
                                                : "0"
                                        }
                                        onChange={(e) =>
                                            setPorcentajePrimerPago(
                                                e.target.value
                                            )
                                        }
                                        placeholder="Ej: 30"
                                        className="w-full my-2 text-sm bg-transparent border-none outline-none focus:outline-none 2xl:my-3 2xl:text-base placeholder:text-gray-400"
                                    />
                                </div>
                                <span className="text-sm text-gray-600">%</span>
                            </div>
                            <p className="mt-1 text-xs text-gray-500">
                                Recomendado entre 20% y 50%. El resto se paga en
                                el establecimiento.
                            </p>
                        </div>

                        <div className="flex justify-center pt-2">
                            <button
                                disabled={saving}
                                onClick={handleSave}
                                className="p-3 px-5 text-white border rounded-lg bg-[#1C4CBB] hover:scale-105 disabled:opacity-60 2xl:text-base text-sm">
                                {saving ? "Guardando..." : "Guardar cambios"}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </ResponsiveModalAbsolute>
    );
};
